import { messages as msgApi, uploadFile, mediaUrl } from '../api.js';
import { navigate } from '../utils/router.js';
import { esc, avatarUrl, formatTime, toast, badgeHtml } from '../utils/dom.js';

const MAX_FILE = 3 * 1024 * 1024;

export async function renderMessages(container, peerGid, { currentUser } = {}) {
  container.innerHTML = `<div class="center-box"><div class="spinner"></div></div>`;

  try {
    const convRes = await msgApi.conversations();
    const conversations = convRes.items || [];

    container.innerHTML = `
      <div class="msg-layout">
        <div class="msg-list ${peerGid ? 'with-thread' : ''}" id="msg-list"></div>
        <div class="msg-thread ${peerGid ? '' : 'hide-mobile'}" id="msg-thread">
          ${
            peerGid
              ? ''
              : `<div class="empty-state" style="flex:1"><p>选择一个会话，或从互关好友资料页发起私聊</p></div>`
          }
        </div>
      </div>
    `;

    const listEl = container.querySelector('#msg-list');
    if (!conversations.length) {
      listEl.innerHTML = `<div class="empty-state"><p>暂无私信<br/><span class="text-sm faint">仅互相关注的用户可私聊</span></p></div>`;
    } else {
      conversations.forEach((c) => {
        const peer = c.peer || {};
        const last = c.last_message || {};
        const row = document.createElement('div');
        row.className = 'user-row';
        if (peerGid && String(peer.gid) === String(peerGid)) {
          row.style.background = 'var(--primary-soft)';
        }
        row.innerHTML = `
          <img src="${esc(avatarUrl(peer))}" alt="" />
          <div style="flex:1;min-width:0">
            <div class="fw-600">${esc(peer.nickname || '用户')}${
              badgeHtml(peer)
            }${c.unread ? ` <span class="badge-dot"></span>` : ''}</div>
            <div class="text-sm muted" style="white-space:nowrap;overflow:hidden;text-overflow:ellipsis">
              ${esc(last.content || (last.file_url ? '[文件]' : ''))}
            </div>
          </div>
          <div class="text-xs faint">${esc(formatTime(last.created_at))}</div>
        `;
        row.addEventListener('click', () => navigate(`/messages/${peer.gid}`));
        listEl.appendChild(row);
      });
    }

    if (peerGid) {
      await renderThread(container.querySelector('#msg-thread'), peerGid, { currentUser });
    }
  } catch (err) {
    container.innerHTML = `<div class="empty-state"><p style="color:var(--danger)">${esc(
      err.message || '加载失败'
    )}</p></div>`;
  }
}

async function renderThread(threadEl, peerGid, { currentUser }) {
  threadEl.innerHTML = `<div class="center-box"><div class="spinner"></div></div>`;
  try {
    const data = await msgApi.thread(peerGid, { limit: 50 });
    const peer = data.peer || { gid: peerGid };
    const items = data.items || [];
    const meId = currentUser?.id;

    threadEl.innerHTML = `
      <div class="msg-thread-header">
        <button type="button" class="btn btn-ghost btn-sm" id="msg-back" style="margin-right:0.5rem">←</button>
        ${esc(peer.nickname || peerGid)}
        <span class="text-sm muted" style="font-weight:400"> · GID ${esc(peer.gid)}</span>
      </div>
      <div class="msg-messages" id="msg-messages"></div>
      <div class="msg-compose">
        <label class="btn btn-ghost btn-sm" title="上传文件（&lt;3MB）">
          📎
          <input type="file" id="msg-file" class="sr-only" />
        </label>
        <textarea id="msg-text" rows="1" placeholder="输入消息…" maxlength="5000"></textarea>
        <button type="button" class="btn btn-primary btn-sm" id="msg-send">发送</button>
      </div>
    `;

    threadEl.querySelector('#msg-back')?.addEventListener('click', () => navigate('/messages'));

    const box = threadEl.querySelector('#msg-messages');
    items.forEach((m) => {
      box.appendChild(bubbleEl(m, meId));
    });
    box.scrollTop = box.scrollHeight;

    let pendingFile = null;

    threadEl.querySelector('#msg-file')?.addEventListener('change', (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (file.size > MAX_FILE) {
        toast('文件需小于 3MB', 'error');
        e.target.value = '';
        return;
      }
      pendingFile = file;
      toast(`已选择：${file.name}`, 'info');
    });

    const send = async () => {
      const text = threadEl.querySelector('#msg-text').value.trim();
      if (!text && !pendingFile) return;
      const btn = threadEl.querySelector('#msg-send');
      btn.disabled = true;
      try {
        let file_url = null;
        let file_name = null;
        if (pendingFile) {
          const up = await uploadFile(pendingFile, { type: 'file' });
          file_url = up.url;
          file_name = up.file_name || pendingFile.name;
          pendingFile = null;
          threadEl.querySelector('#msg-file').value = '';
        }
        const res = await msgApi.send({
          to_gid: peerGid,
          content: text || null,
          file_url,
          file_name,
        });
        const msg = res.message || res;
        box.appendChild(
          bubbleEl(
            {
              ...msg,
              from_id: meId,
              content: text,
              file_url,
              file_name,
              created_at: msg.created_at || new Date().toISOString(),
            },
            meId
          )
        );
        threadEl.querySelector('#msg-text').value = '';
        box.scrollTop = box.scrollHeight;
      } catch (err) {
        toast(err.message || '发送失败', 'error');
      } finally {
        btn.disabled = false;
      }
    };

    threadEl.querySelector('#msg-send')?.addEventListener('click', send);
    threadEl.querySelector('#msg-text')?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        send();
      }
    });
  } catch (err) {
    threadEl.innerHTML = `<div class="empty-state"><p style="color:var(--danger)">${esc(
      err.message || '无法打开会话'
    )}</p>
    <p class="text-sm muted">私聊需要双方互相关注</p></div>`;
  }
}

function bubbleEl(m, meId) {
  const mine = meId != null && String(m.from_id) === String(meId);
  const div = document.createElement('div');
  div.className = `msg-bubble ${mine ? 'mine' : 'theirs'}`;
  let html = '';
  if (m.content) html += `<div>${esc(m.content)}</div>`;
  if (m.file_url) {
    const href = mediaUrl(m.file_url);
    html += `<div style="margin-top:0.25rem"><a href="${esc(href)}" target="_blank" rel="noopener" style="color:inherit;text-decoration:underline">📎 ${esc(
      m.file_name || '文件'
    )}</a></div>`;
  }
  html += `<div class="text-xs" style="opacity:0.7;margin-top:0.25rem">${esc(
    formatTime(m.created_at)
  )}</div>`;
  div.innerHTML = html;
  return div;
}
